/**
 * The vocabulary of the referral and affiliate programs.
 *
 * Shared by the Worker, the app and the partner portal so a status shown on a
 * screen and a status checked in SQL cannot drift apart. Everything numeric
 * here is a DEFAULT — the live value is a `directory_settings` row, because
 * every one of these is a commercial decision somebody will want to change
 * without a deploy.
 */

// ---------------------------------------------------------------------------
// Customer referrals
// ---------------------------------------------------------------------------

export const REFERRAL_STATUSES = [
  'created',
  'clicked',
  'signed_up',
  'trial_active',
  'payment_pending',
  'qualified',
  'rewarded',
  'disqualified',
  'reversed',
] as const;
export type ReferralStatus = (typeof REFERRAL_STATUSES)[number];

/** The states a referrer sees as "still in progress" rather than settled. */
export const REFERRAL_PENDING_STATUSES: readonly ReferralStatus[] = [
  'created',
  'clicked',
  'signed_up',
  'trial_active',
  'payment_pending',
];

export const REFERRAL_CREDIT_ENTRY_TYPES = [
  'referral_reward',
  'admin_credit',
  'credit_redemption',
  'credit_reversal',
  'expired_credit',
] as const;
export type ReferralCreditEntryType = (typeof REFERRAL_CREDIT_ENTRY_TYPES)[number];

export const DEFAULT_REFERRER_REWARD_MONTHS = 1;
export const DEFAULT_REFERRED_TRIAL_DAYS = 30;

/**
 * The random part of a referral code: `chris-x8k2p4`.
 *
 * Six characters from a 32-symbol alphabet is a billion possibilities, which
 * is not a secret but is emphatically not enumerable — and enumerability is
 * the whole risk here, because a walkable code space is a customer list.
 */
export const REFERRAL_CODE_RANDOM_LENGTH = 6;

/** Risk signals on a customer referral. Flags prompt review; they never block on their own. */
export const REFERRAL_RISK_FLAGS = [
  'possible_self_referral',
  'same_payment_instrument',
  'high_signup_velocity',
  'duplicate_business',
  'suspicious_email',
  'disposable_email',
  'reward_abuse_pattern',
  'shared_network',
] as const;
export type ReferralRiskFlag = (typeof REFERRAL_RISK_FLAGS)[number];

// ---------------------------------------------------------------------------
// Affiliate partners
// ---------------------------------------------------------------------------

export const AFFILIATE_STATUSES = [
  'application_pending',
  'active',
  'suspended',
  'rejected',
  'terminated',
  'payout_hold',
  'compliance_review',
] as const;
export type AffiliateStatus = (typeof AFFILIATE_STATUSES)[number];

/** The statuses that may earn new commission. Everything else accrues nothing. */
export const AFFILIATE_EARNING_STATUSES: readonly AffiliateStatus[] = ['active', 'payout_hold', 'compliance_review'];

/**
 * `payout_hold` and `compliance_review` earn but are not PAID.
 *
 * Keeping the two ideas apart matters: a partner under review has still
 * genuinely referred paying customers, and stopping the accrual would mean
 * silently confiscating money that a completed review would have found owed.
 * The hold belongs at the payout, where it can be lifted.
 */
export const AFFILIATE_PAYABLE_STATUSES: readonly AffiliateStatus[] = ['active'];

export const AFFILIATE_RISK_STATES = ['normal', 'review', 'high_risk', 'payout_hold', 'suspended'] as const;
export type AffiliateRiskState = (typeof AFFILIATE_RISK_STATES)[number];

export const AFFILIATE_PARTNER_TYPES = [
  'cleaning_consultant',
  'cleaning_company',
  'content_creator',
  'business_coach',
  'bookkeeper',
  'software_consultant',
  'industry_organization',
  'publisher',
  'influencer',
  'other',
] as const;
export type AffiliatePartnerType = (typeof AFFILIATE_PARTNER_TYPES)[number];

export const COMMISSION_ENTRY_TYPES = [
  'commission_earned',
  'commission_reversal',
  'refund_reversal',
  'chargeback_reversal',
  'admin_adjustment',
  'bonus',
  'correction',
  'payout',
  'payout_reversal',
] as const;
export type CommissionEntryType = (typeof COMMISSION_ENTRY_TYPES)[number];

export const PAYOUT_STATUSES = [
  'open',
  'calculated',
  'under_review',
  'approved',
  'processing',
  'paid',
  'partially_failed',
  'failed',
  'held',
  'cancelled',
] as const;
export type PayoutStatus = (typeof PAYOUT_STATUSES)[number];

export const PAYOUT_ITEM_STATUSES = [
  'pending',
  'held',
  'sent',
  'paid',
  'unclaimed',
  'failed',
  'returned',
  'cancelled',
  'carried_forward',
] as const;
export type PayoutItemStatus = (typeof PAYOUT_ITEM_STATUSES)[number];

export const ATTRIBUTION_REQUEST_STATUSES = [
  'pending',
  'auto_approved',
  'approved',
  'rejected',
  'conflict',
  'withdrawn',
] as const;
export type AttributionRequestStatus = (typeof ATTRIBUTION_REQUEST_STATUSES)[number];

export const DEFAULT_ATTRIBUTION_WINDOW_DAYS = 90;
export const DEFAULT_CLAIM_WINDOW_DAYS = 30;
export const DEFAULT_MINIMUM_PAYOUT_CENTS = 2500;
export const DEFAULT_PAYOUT_DAY = 15;
export const AFFILIATE_STARTING_RATE_BP = 2000;
export const AFFILIATE_MAX_RATE_BP = 5000;

/** Basis points, everywhere. 10000 = 100%, so 30% is 3000. */
export const BASIS_POINTS = 10_000;

/**
 * The first-party cookies that carry attribution before an account exists.
 *
 * First-party and set by this deployment, holding a random visitor id and the
 * partner or referral code — not a fingerprint, and nothing that identifies a
 * person. Once an account exists the attribution is copied server-side and the
 * cookie stops mattering, which is what makes attribution survive a browser
 * that clears them.
 */
export const ATTRIBUTION_COOKIE_NAME = 'bc_attr';
export const VISITOR_COOKIE_NAME = 'bc_vid';

/** How long the attribution cookie itself lives; the server-side window is what decides eligibility. */
export const ATTRIBUTION_COOKIE_DAYS = 90;

/** The commission month a moment falls in, as `YYYY-MM` (UTC). */
export function commissionPeriodOf(isoTimestamp: string): string {
  return isoTimestamp.slice(0, 7);
}

/**
 * Commission on an amount, in cents, rounded half-up.
 *
 * Integer arithmetic throughout: `Math.round(99_00 * 0.3)` is fine until the
 * day a rate is 33.33% and floating point turns a partner's cent into a
 * reconciliation ticket. Half-up rather than banker's rounding because it is
 * what a partner reading the arithmetic on their own screen expects.
 */
export function commissionCents(grossCents: number, rateBp: number): number {
  return Math.round((grossCents * rateBp) / BASIS_POINTS);
}
